"use client"
import styled from "styled-components"
import { motion } from "framer-motion"

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
`

const LogoIconWrapper = styled(motion.div)`
  background: linear-gradient(135deg, var(--accent-blue) 0%, #6C9CFF 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(79, 141, 251, 0.3);
  overflow: hidden;
`

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`

const LogoText = styled(motion.div)`
  font-weight: 700;
  font-size: 1.5rem;

  span.highlight {
    color: var(--accent-blue);
  }
`

const SynthlinxLogo = ({ size = "default" }) => {
  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
    hover: {
      scale: 1.03,
      transition: { duration: 0.3 },
    },
  }

  const iconVariants = {
    initial: { scale: 0, rotate: -20 },
    animate: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
      },
    },
    hover: {
      rotate: [0, -10, 10, -5, 5, 0],
      transition: { duration: 0.5 },
    },
  }

  const textVariants = {
    initial: { opacity: 0, y: 10 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  }

  const iconSize = size === "small" ? "24px" : size === "large" ? "48px" : "36px"
  const fontSize = size === "small" ? "1.2rem" : size === "large" ? "1.8rem" : "1.5rem"

  const imageSrc = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBwgHBgkIBwgKCgkLDRYPDQwMDRsUFRAWIB0iIiAdHx8kKDQsJCYxJx8fLT0tMTU3Ojo6Iys/RD84QzQ5OjcBCgoKDQwNGg8PGjclHyU3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3Nzc3N//AABEIALwAyAMBIgACEQEDEQH/xAAbAAEAAwADAQAAAAAAAAAAAAAAAQIHAwUGBP/EAEQQAAECAwEKCgcHAwUAAAAAAAABAgMEBREGByExNlR0k7LREhUWQVFTVWGRsSIzQnFykqMTMjVzgaHBFDThIyVSovD/xAAbAQEAAgMBAQAAAAAAAAAAAAAAAgQBAwUGB//EAC4RAAICAQEGBAUFAQAAAAAAAAABAgMRBAUSEzFBURQhMlIGFSJhcUJikaHRM//aAAwDAQACEQMRAD8A6i/flm3Q2bTzPzQL+GWjdDZtPM/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAANAvIZaO0OJtMAvIZZu0N+0wAC/flozQ4e08z80C/flpD0OHtPM/AAAAAAAB2MvQqnMwGxoErwob0tavDalv7nXGlXO4aLKW9WW9JRG6TUjVbNxWUeL5N1fM/qs3jk3V8z+qzeaMhZC98ur7spy1c0ZxyZrGZ/UZvHJmsZkusZvNJRVJRV6THy+vuaJbQsXQzbkxWMz+ozeOTFZzFdYzeaWilk96+JjwFfcry2rauiMzS5etZiutZvCXLVrMvqs3mmoWQw9DX3NMts3rojMeStazJNazeTyTreZprWbzTkUshHwUO5Xlt7ULojL+SdbzL6rN5PJKt5l9Vm81FCTHgodzS/iPUr9K/v8A0yt1ydbRqr/Rpgwr/qs3nS4lwm1xvUP+FfIxV/3ne8q6ilVYwdrZG0bNapcRYwQACsdo0C8flo7Q37TALyGWbtDftMAAv4ZaN0OHtPM/NBv35aN0OHtPM+AAAAAAABpVz34LKfl/ypmppNzy/wCyynwfyp0tnet/gr6j0nZoWQqhZDrs5kyULIVQshEpzLFkKoWIFOZKFkKtttxFkIsqTJQshVCyEWU5lkJIQsYKsk8nHG9Q/wCFfIxV33ne82uP6iJ8K+RijvvO95ztb0PVfDK8pkAAonqjQLyGWbtDftMAvIZZu0N+0wAC/hlo3Q4e08z80C/flo3Q4e08z8AAAAAAAHq7l682G1khOqjWYoUTo7lPKA202yqnvIjOCksM1xP1TnRSyHjrl7ouDwZKoPwYocVebuU9jz9HOd6q6Nsd5HJug4PzJQsVQsbChMsh8dWqcvSpVY0w7D7DOd69BFWqUvSpVY0w7DiYxMb17jNarUo9TmljzDsPstTE1ClqdSqlhczZpdE75ZlyPqm7o6pMR3REmnwkVbUaxcCIcXHtVz6N8x13ODku2b6ndWnqSxuo7Lj6rZ/G+Ycf1bP43idaDHEl3Hh6vajsuUFWz+N4k8oKvn8bxOsA35dzHhaPYv4OydX6s5qtWfjWLgxnWrhAIuTfMnCqFfpWAADBsNAvIZaP0N+0wC8hlo7Q37TAATfvy0bocPaeZ8aDfvy0bocPaeZ8AAAiKtnSuAAA5klJjqIvyKSklN5tF+RdxPcl2M4ZwA+hJKbzWL8jtwSRm81jatdw3JdjGD5z11y90SNRsjUH+iuCHEVcXcp5lJGczSPq13EpITmaR9Wu42UysqllI1WwjNYZqyWfrzHyVWpS9LlVjzDsPssTG5ehDzVGr01T5N8Opy0dWsS2G9WfsqnnKpUY9Tmljx3WYfRansodG3WqMPLmc2Gicp/VyQqlSj1OaWPMOw4mtTE1D4wDkSk5PLOrGKisIAAwSAGKy23wO4p1z81OwPtlckJqrY3hpjJ11yseIoHTg9CtysfOYXgpC3LR85heCm7wd3tJqEnyPPg719zUdrVd/UMsRP8Aip0SmqyqdfqWDEoSjzAANZE0C8hlo/Q4m0wC8hlm7Q37TAAL9+WjNDh7TzPzQL9+WbdDZtPM/ABeB6+H8SeZQvA9ez4k80JQ9SMx5o0NpdqJ0FGnI09MksHRmciHI1DjaciGcI59pyNJjRYctCdGmHNZDalrlXmOKNHhS8J8aO9GQ2parlPB1+sxKpF4DbWS7V9BvT3qV9RfGmP3Km45Mm6CuRKpF4DeEyWavoMtx96nUDmsBwZzc5bzLEYqKwgACBkC1OcWollvuPS3N3PrNOZNzicGAi2sYuN/+DbVVK2WIkXJLzZS5ygrMq2anERINtrGr7f+D2C4EsTAllliF7EbYjUsRMSJzFHHfoojTHCMQe88nG441ORxxuNxerOGN6t3wr/JnamiRvVO+FTO1OTtL9I1PQAA5ZUNAvIZaP0N+0wC8hlo/Q37TAAL9+WjdEZtPM/NAv35aN0Nm08z8AF4Hr2fEnmhQlF4DkcmNMJmLw8mU/M0VpyNPF8op9OeH8pPKSoJzw/lO0toVFuV8We3aIsaHLwVix3oxjcKuU8VylqSc8L5D5KhVpyoMa2ZcnBbia1thGe0IJfSVZvJ9FdrMSpRVa21ku1fRZ096nUgHJsslOW9IilgAAgAAdvc/Q41Wj8JbWSzF9OInP3J3koQc3hEZzjCO9I+y5qhsmnJNzz2tgovoMVyWuXp9x7P7WE1LEcxETmRyHmLorm0gw/6imoqNYlj4SLb+qHkeE6zHbzYzoq7wv0OJWhi76kzUljQutZ4oUWJD6xvzIZhwndK+ItXpUl8y/aWYxwaUsWH1jfFCixIfWN8UM44TulRavSviY+ZftN8bMdDQIsRn2brHsxL7SGfKTatllq+JBV1Op42PLAtt3wACqajQLyGWj9DftMBN5DLR2hv2mAAX78tG6HD2nmfGg378tG6HD2nmfAAAAAAAAAAAAAAA7e56hxqtHtdayWavpxE8k7yUISm8IhZZGuO9IXP0OLVo9qqrJZi+nEsx9yd5ostLwZSA2BLsRkNqYGoTLS8KUgNgS7EZDamBEOTEdvT6dVL7nBv1Mr5fYhVU8fdRc7arp6nswphiQk5+9D16lTbbTG2OGbKLHW8oyLD3Wg9fdRc9wuFO09mFMMSGiY+9DyHP+1hwrqZVPDO1XNTWUAAaiYAAAAABoF5DLR2hv2mAXkMtH6HE2mAAX78tG6HD2nmfmg378tG6HD2nmfAAljHRHtYxLXOWxEIOaR/vIH5jfNDK82Rm8RbR3PI6s9SzWN3jkdWepZrG7zTwp0lpIHi5fEWqT5IzBbj6x1LNY3eRyPrPUs1jd5p6lVM+DrJx2/qXzSMyW5CsdSzWN3kckax1LNY3eaYpCmfBVliO2730RnkhcdPxJpqTiJCgJhcqORVVO491Ky0KUgsgQGIyG1MCIc5VSxTRCrkQu1lmo9RGIqpZSpZRmsqpCkqQpIuQKqePusocOEx0/LWNTB9oz+UPYHTXWfgcf3t8zRqYRnW8l+htS8jPAAeeOkAAAAAAaBeQyzdob9pgF5DLN2hv2mAAm/flo3Q4e08z40G/flo3Q4e08z4AHNI/wB5A/Mb5ocJzSKKs7L98RvmhmPMhb6H+DaALU6R+v7HcXI+XST3mQpVSyr/AOsKqSRtgiqlVLKVUki5BMhSpZSqr3ki3AhSqllKqSRcrTKqQpaz3eJVV7k8SRbgVU6a6z8Dj+9vmh3KqnSh011mChTHdZh/VDXf/wApfgvU+pGeAA84dMAAAAAA0C8hlm7Q37TALyGWjtDftMABN+/LRuhw9p5nxoF+/LNuhs2nmfgAIqtVFatiotqKgAHM+/juq9pTeudvHHdV7Sm9c7efACW9LuauBV7Uffx1Ve0pvXO3jjqq9ozeudvPgA35dxwKvaj7uOap2jN6528ccVPtGb1zt58IG/LuZ4NftR9vHFT7Rm9c7eON6n2jN6928+IDfl3HCr7H28b1LtCb1zt5HG1S7Qm9e7efGDPEl3M8OHY+zjapdoTeudvHGtS7Qmtc7efGBxJ9zO5HsfZxtUs/mtc7eccafnI7FZGmo72rjR0RVQ+cBzk+plRSAAIGQAAAAADQLyGWbtDftMAvIZZu0N+0wAC/flm3Q2bTzPzQb9ScO7ViLmrEwfE4z8AgE2CwAgE2CwAgE2EAAAAAE2CwAgE2CwAgAmwAgE2E2AFQTYLACATYTYAVBawiwA0C8hlo/Q4m0wC8p6N2r0TNXp/2aSAf/9k="

  return (
    <LogoContainer variants={containerVariants} initial="initial" animate="animate" whileHover="hover">
      <LogoIconWrapper variants={iconVariants} style={{ width: iconSize, height: iconSize }}>
        <LogoImage src={imageSrc} alt="Logo" />
      </LogoIconWrapper>

      <LogoText variants={textVariants} style={{ fontSize }}>
        Synth<span className="highlight">linx</span>
      </LogoText>
    </LogoContainer>
  )
}

export default SynthlinxLogo
